import {
  getElecDailyProductsChart,
  getElecDayAheadPrices,
} from "$/api/market-monitoring/electricity.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import { GraphIndicators } from "$/lib/components/graphs/GraphIndicator/GraphIndicators";
import LineGraph from "$/lib/components/graphs/LineGraph";
import { Tabs } from "$/lib/components/molecules";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import { DEFAULT_GRAPH_TABS } from "$/lib/utils/constants/marketMonitoring/graphs.constants";
import {
  getDate,
  getLast6MonthsStartAndEndDates,
} from "$/lib/utils/functions/date.functions";
import { cn } from "$/lib/utils/functions/misc.functions";
import { handleOnChangeDefaultGraphsTabs } from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";

type Props = {
  selectedDate: Date;
} & CustomDashboardProps;

const DailyProductsChart = ({
  id,
  selectedDate: defaultSelectedDate,
  isShowcase = false,
  isDraggable = false,
}: Props) => {
  const [selectedDate, setSelectedDate] = useState<Date>(defaultSelectedDate);

  const [selectedGraphTabId, setSelectedGraphTabId] = useState("LAST_6_MONTHS");

  const { start, end } = getLast6MonthsStartAndEndDates();

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const [indices, setIndices] = useState<
    | {
        start: number;
        end: number;
      }
    | undefined
  >();

  const { data, isLoading } = useQuery({
    queryKey: ["get-elec-daily-prod-chart", dateRange],
    queryFn: () =>
      getElecDailyProductsChart({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
      }),
  });

  const { data: dayAheadPrices } = useQuery({
    queryKey: ["get-elec-day-ahead-prices", selectedDate],
    queryFn: () => getElecDayAheadPrices({ date: getDate(selectedDate) }),
  });

  const toDisplay = useMemo(() => (data ? data.data : []), [data]);
  const toDisplayVolatility = useMemo(
    () => (data ? data.volatilityData : []),
    [data],
  );

  const keys = useMemo(() => ["Moy. Spot DA BASE", "Moy. Spot DA PEAK"], []);

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  const colors: Record<string, string> = {
    "Moy. Spot DA BASE": "#5978a8",
    "Moy. Spot DA PEAK": "#d45359",
  };

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  useEffect(() => {
    setGraphVisibility(
      keys.map((key) => ({
        name: key,
        isVisible: true,
        color: key === "Moy. Spot DA BASE" ? "#5978a8" : "#d45359",
      })),
    );
  }, [keys]);

  useEffect(() => {
    setIndices(undefined);
  }, [dateRange]);

  return (
    <DisplayBox
      title="Spot day ahead"
      Icon={LineChartIcon}
      theme="blue"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      className="rounded-xl shadow-displayBoxShadow"
      // isLoading={isLoading || isLoadingPrices}
      MiddleComponent={
        <Tabs
          className="mx-auto"
          type="bordered"
          tabs={DEFAULT_GRAPH_TABS}
          activeTab={selectedGraphTabId}
          handleTabChange={setSelectedGraphTabId}
        />
      }
      id={id}
      isDraggable={isDraggable}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
    >
      <ChartsContainer>
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <Flexbox fullWidth fullHeight className="gap-2 rounded-3xl">
            <Flexbox fullWidth className="h-[65%]">
              <LineGraph
                data={toDisplay}
                graphVisibility={graphVisibility}
                title="Prix Stlt EEX"
                xAxisDataKey="date"
                syncId="id-elec-daily"
                colors={colors}
                theme="blue"
                keys={keys}
                curveType="step"
                heightPercent={65}
                leftMargin={-10}
                indices={indices}
              />
            </Flexbox>

            <Flexbox fullWidth className="h-[35%]">
              <LineGraph
                data={toDisplayVolatility}
                title="Volatilité"
                graphVisibility={graphVisibility}
                syncId="id-elec-daily"
                withBrush
                xAxisDataKey="date"
                colors={colors}
                theme="blue"
                keys={keys}
                curveType="step"
                yAxisTicksNumber={7}
                heightPercent={40}
                leftMargin={-10}
                setIndices={setIndices}
                indices={indices}
              />
            </Flexbox>
          </Flexbox>
        )}

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
          />

          <GraphIndicators
            selectedDate={selectedDate}
            data={
              dayAheadPrices
                ? [
                    {
                      title: "Spot DA",
                      baseloadItem: dayAheadPrices.spotElecAvg_dailyProduct,
                      peakloadItem: dayAheadPrices.spotElecAvgPeak_dailyProduct,
                    },
                  ]
                : []
            }
            indicatorsTitles={{
              title1: "Base",
              title2: "Peak",
            }}
          />

          <GraphFilter
            title="Légende"
            data={graphVisibility}
            handleOnChange={handleOnChangeGraphVisiblity}
          />
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default DailyProductsChart;
